// File: src/pages/Ledgers/LedgerReport.jsx
// حركة الصندوق والبنك — بتصميم التقارير الموحّد (ReportKit)
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import SouthWestRoundedIcon from "@mui/icons-material/SouthWestRounded";
import NorthEastRoundedIcon from "@mui/icons-material/NorthEastRounded";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import { toast } from "react-toastify";

import axios from "../../axios";
import { SettingsContext } from "../../contexts/SettingsContext";
import {
  ReportPage, ReportFilters, ReportSelect, ReportTableCard,
  useReportDateRange, useAllowedBranches, reportGridSx, fmtMoney, fmtNumber, REPORT_COLORS,
} from "../../components/Reports/ReportKit";

const EMPTY_REPORT = { openingBalance: 0, totalIn: 0, totalOut: 0, closingBalance: 0, items: [] };

// أنواع السندات (ليست ضمن أنواع الفواتير)
const VOUCHER_NAMES = {
  101: "سند قبض من عميل",
  102: "سند صرف لمورد",
  103: "سند قبض عام",
  104: "سند صرف عام",
};

const pick = (src, ...keys) => {
  for (const k of keys) if (src?.[k] !== undefined && src?.[k] !== null) return src[k];
  return undefined;
};

function normalizeRow(src, i) {
  return {
    id: i + 1,
    txDate: pick(src, "txDate", "TxDate"),
    referenceId: pick(src, "referenceId", "ReferenceId"),
    invoiceNumber: pick(src, "invoiceNumber", "InvoiceNumber"),
    invoiceTypeId: pick(src, "invoiceTypeId", "InvoiceTypeId", "type", "Type", "voucherType", "VoucherType"),
    notes: pick(src, "notes", "Notes"),
    amountIn: Number(pick(src, "amountIn", "AmountIn") ?? 0),
    amountOut: Number(pick(src, "amountOut", "AmountOut") ?? 0),
    runningBalance: Number(pick(src, "runningBalance", "RunningBalance") ?? 0),
  };
}

const fmtDateTime = (v) => {
  if (!v) return { date: "", time: "" };
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return { date: String(v), time: "" };
  const time = d.getHours() || d.getMinutes()
    ? d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : "";
  return { date: d.toLocaleDateString("en-GB"), time };
};

export default function LedgerReport() {
  const { t } = useTranslation();
  const companyId = parseInt(localStorage.getItem("companyId") || "0", 10);
  const { decimalPlaces } = useContext(SettingsContext);
  const dp = decimalPlaces ?? 2;
  const money = useCallback((v) => fmtMoney(v, dp), [dp]);

  const LEDGERS = useMemo(() => ({
    safe: {
      label: t("ledgerReport.safeMovement", "حركة الصندوق"),
      short: t("ledgerReport.safe", "الصندوق"),
      endpoint: "/ledgers/safe-ledger",
      icon: <AccountBalanceWalletIcon />,
    },
    bank: {
      label: t("ledgerReport.bankMovement", "حركة البنك"),
      short: t("ledgerReport.bank", "البنك"),
      endpoint: "/ledgers/bank-ledger",
      icon: <AccountBalanceIcon />,
    },
  }), [t]);

  const [type, setType] = useState("safe");
  const ledger = LEDGERS[type];

  const range = useReportDateRange("month");
  const { branchOptions, defaultBranch, ready: branchesReady, branchName } = useAllowedBranches(companyId);
  const [branchId, setBranchId] = useState("");
  const [accountId, setAccountId] = useState("");   // الصندوق أو البنك

  useEffect(() => { if (branchesReady) setBranchId(defaultBranch); }, [branchesReady, defaultBranch]);

  // ── القوائم
  const [safes, setSafes] = useState([]);
  const [banks, setBanks] = useState([]);
  const [typeMap, setTypeMap] = useState({});

  useEffect(() => {
    if (!companyId) return;
    axios.get(`/safe/by-company/${companyId}`).then(r => setSafes(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get(`/bank/by-company/${companyId}`).then(r => setBanks(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get(`/InvoiceTypes`, { params: { companyId } })
      .then(r => {
        const map = {};
        (r.data || []).forEach(it => {
          const id = pick(it, "invoiceTypeId", "InvoiceTypeId", "id", "Id");
          const name = pick(it, "name", "Name", "nameAr", "NameAr");
          if (id != null && name) map[String(id)] = name;
        });
        setTypeMap(map);
      })
      .catch(() => {});
  }, [companyId]);

  const accountOptions = useMemo(() => {
    const list = type === "safe" ? safes : banks;
    return list.map(a => ({
      value: String(pick(a, "safeId", "SafeId", "id", "Id")),
      label: pick(a, "name", "Name", "nameAr", "NameAr") ?? "",
    }));
  }, [type, safes, banks]);

  // ── التقرير
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(EMPTY_REPORT);

  const fetchReport = useCallback(async () => {
    if (!companyId) {
      toast.error(t("common.companyIdRequired", "رقم الشركة مطلوب"));
      return;
    }
    if (!branchesReady) return;
    setLoading(true);
    try {
      const params = {
        companyId,
        from: `${range.dateFrom}T00:00:00`,
        to: `${range.dateTo}T23:59:59`,
      };
      if (branchId) params.branchId = Number(branchId);
      if (accountId) params[type === "safe" ? "safeId" : "bankId"] = Number(accountId);

      const { data } = await axios.get(LEDGERS[type].endpoint, { params });
      setReport({
        openingBalance: Number(pick(data, "openingBalance", "OpeningBalance") ?? 0),
        totalIn: Number(pick(data, "totalIn", "TotalIn") ?? 0),
        totalOut: Number(pick(data, "totalOut", "TotalOut") ?? 0),
        closingBalance: Number(pick(data, "closingBalance", "ClosingBalance") ?? 0),
        items: (pick(data, "items", "Items") || []).map(normalizeRow),
      });
    } catch {
      toast.error(t("common.failedToLoadReport", "تعذّر تحميل التقرير"));
      setReport(EMPTY_REPORT);
    } finally {
      setLoading(false);
    }
  }, [companyId, branchesReady, range.dateFrom, range.dateTo, branchId, accountId, type, LEDGERS, t]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const onTypeChange = (_, v) => {
    if (!v || v === type) return;
    setType(v);
    setAccountId("");
    setReport(EMPTY_REPORT);
  };

  const typeName = useCallback((id) => {
    if (id == null || id === "") return "";
    return typeMap[String(id)] || VOUCHER_NAMES[id] || `${t("common.type", "نوع")} ${id}`;
  }, [typeMap, t]);

  // صف الرصيد الافتتاحي أعلى الحركات
  const rows = useMemo(() => [
    {
      id: 0, isOpening: true, notes: t("ledgerReport.openingBalance", "رصيد افتتاحي"),
      amountIn: 0, amountOut: 0, runningBalance: report.openingBalance,
    },
    ...report.items,
  ], [report, t]);

  const columns = useMemo(() => [
    {
      field: "txDate", headerName: t("ledgerReport.date", "التاريخ"), minWidth: 120, flex: .8,
      headerAlign: "center", align: "center", sortable: false,
      renderCell: (p) => {
        if (p.row.isOpening) return <Typography sx={{ fontSize: 13, color: "#64748b", direction: "ltr" }}>{fmtDateTime(range.dateFrom).date}</Typography>;
        const { date, time } = fmtDateTime(p.value);
        return (
          <Box sx={{ lineHeight: 1.25, textAlign: "center" }}>
            <Typography sx={{ direction: "ltr", fontSize: 14 }}>{date}</Typography>
            {time && <Typography sx={{ direction: "ltr", fontSize: 11.5, color: "#94a3b8" }}>{time}</Typography>}
          </Box>
        );
      },
    },
    {
      field: "invoiceTypeId", headerName: t("ledgerReport.type", "النوع"), minWidth: 150, flex: 1.1,
      headerAlign: "center", align: "center", sortable: false,
      renderCell: (p) => {
        if (p.row.isOpening) {
          return (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: .5, px: 1.25, py: .35, borderRadius: "999px", fontSize: 12.5, fontWeight: 800, bgcolor: REPORT_COLORS.slate.soft, color: REPORT_COLORS.slate.main }}>
              <FlagOutlinedIcon sx={{ fontSize: 15 }} /> {p.row.notes}
            </Box>
          );
        }
        const name = typeName(p.value);
        return name ? (
          <Box sx={{ px: 1.25, py: .35, borderRadius: "999px", fontSize: 12.5, fontWeight: 700, bgcolor: REPORT_COLORS.primary.soft, color: REPORT_COLORS.primary.main, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {name}
          </Box>
        ) : "—";
      },
    },
    {
      field: "invoiceNumber", headerName: t("ledgerReport.invoiceNumber", "رقم المستند"), minWidth: 110, flex: .7,
      headerAlign: "center", align: "center", sortable: false,
      renderCell: (p) => p.row.isOpening ? "" : (
        <Typography sx={{ fontWeight: 800, color: REPORT_COLORS.primary.main, direction: "ltr", fontSize: 14 }}>{p.value ?? "—"}</Typography>
      ),
    },
    {
      field: "notes", headerName: t("ledgerReport.notes", "البيان"), minWidth: 200, flex: 1.8,
      headerAlign: "center", align: "center", sortable: false,
      renderCell: (p) => p.row.isOpening ? "" : (
        <Typography title={p.value || ""} sx={{ fontSize: 13.5, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%", textAlign: "center" }}>
          {p.value || "—"}
        </Typography>
      ),
    },
    {
      field: "amountIn", headerName: t("ledgerReport.incoming", "وارد"), minWidth: 120, flex: .9,
      headerAlign: "center", align: "center", sortable: false,
      renderCell: (p) => p.value ? (
        <Typography sx={{ direction: "ltr", fontWeight: 700, fontSize: 14, color: REPORT_COLORS.success.main }}>{money(p.value)}</Typography>
      ) : <Typography sx={{ color: "#cbd5e1" }}>—</Typography>,
    },
    {
      field: "amountOut", headerName: t("ledgerReport.outgoing", "صادر"), minWidth: 120, flex: .9,
      headerAlign: "center", align: "center", sortable: false,
      renderCell: (p) => p.value ? (
        <Typography sx={{ direction: "ltr", fontWeight: 700, fontSize: 14, color: REPORT_COLORS.error.main }}>{money(p.value)}</Typography>
      ) : <Typography sx={{ color: "#cbd5e1" }}>—</Typography>,
    },
    {
      field: "runningBalance", headerName: t("ledgerReport.runningBalance", "الرصيد"), minWidth: 130, flex: 1,
      headerAlign: "center", align: "center", sortable: false,
      renderCell: (p) => (
        <Typography sx={{ direction: "ltr", fontWeight: 800, fontSize: 14, color: p.value < 0 ? REPORT_COLORS.error.main : "#0f172a" }}>
          {money(p.value)}
        </Typography>
      ),
    },
  ], [t, typeName, money, range.dateFrom]);

  const net = report.totalIn - report.totalOut;
  const activeCount = (accountId ? 1 : 0) + (branchId && branchId !== defaultBranch ? 1 : 0);
  const accountLabel = accountOptions.find(o => o.value === accountId)?.label;

  return (
    <ReportPage
      title={t("ledgerReport.title", "حركة الصندوق والبنك")}
      subtitle={t("ledgerReport.subtitle", "الوارد والصادر والرصيد المتراكم خلال الفترة")}
      icon={<SavingsOutlinedIcon />}
      period={range}
      badges={[
        { label: accountLabel || `${ledger.short}: ${t("common.all", "الكل")}`, icon: ledger.icon },
        ...(branchId ? [{ label: branchName(branchId) ?? "", icon: <StoreOutlinedIcon /> }] : []),
      ]}
      actions={[{ label: t("common.refresh", "تحديث"), icon: <RefreshIcon />, onClick: fetchReport, disabled: loading, primary: true }]}
      stats={[
        { label: t("ledgerReport.openingBalance", "رصيد افتتاحي"), value: money(report.openingBalance), icon: <FlagOutlinedIcon />, color: "slate" },
        { label: t("ledgerReport.totalIncoming", "إجمالي الوارد"), value: money(report.totalIn), icon: <SouthWestRoundedIcon />, color: "success" },
        { label: t("ledgerReport.totalOutgoing", "إجمالي الصادر"), value: money(report.totalOut), icon: <NorthEastRoundedIcon />, color: "error" },
        { label: t("ledgerReport.net", "صافي الحركة"), value: money(net), icon: <SavingsOutlinedIcon />, color: net < 0 ? "warning" : "info" },
        { label: t("ledgerReport.closingBalance", "الرصيد الختامي"), value: money(report.closingBalance), icon: ledger.icon, color: "primary", highlight: true },
      ]}
      filters={
        <ReportFilters
          range={range}
          onApply={fetchReport}
          onReset={() => { range.reset(); setAccountId(""); setBranchId(defaultBranch); }}
          loading={loading}
          activeCount={activeCount}
        >
          <ToggleButtonGroup
            exclusive fullWidth size="small" value={type} onChange={onTypeChange}
            sx={{
              height: 40, bgcolor: "#f1f5f9", p: .4, borderRadius: "10px", gap: .4,
              "& .MuiToggleButton-root": {
                border: 0, borderRadius: "8px !important", fontWeight: 700, fontSize: 13.5, color: "#475569", gap: .6,
                "& svg": { fontSize: 18 },
              },
              "& .Mui-selected": {
                bgcolor: "#fff !important", color: `${REPORT_COLORS.primary.main} !important`,
                boxShadow: "0 1px 3px rgba(15,23,42,.12)",
              },
            }}
          >
            {Object.entries(LEDGERS).map(([key, l]) => (
              <ToggleButton key={key} value={key}>{l.icon}{l.short}</ToggleButton>
            ))}
          </ToggleButtonGroup>

          <ReportSelect
            label={ledger.short}
            value={accountId}
            onChange={setAccountId}
            options={accountOptions}
          />

          {branchOptions.length > 1 && (
            <ReportSelect
              label={t("ledgerReport.branch", "الفرع")}
              value={branchId}
              onChange={setBranchId}
              options={branchOptions}
              allLabel={defaultBranch ? null : t("common.all", "الكل")}
            />
          )}
        </ReportFilters>
      }
    >
      <ReportTableCard
        title={`${t("ledgerReport.movementDetails", "تفاصيل الحركات")} — ${ledger.label}`}
        count={fmtNumber(report.items.length)}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          disableColumnMenu
          getRowHeight={() => 54}
          getRowClassName={(p) => (p.row.isOpening ? "row-opening" : "")}
          initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
          pageSizeOptions={[25, 50, 100]}
          localeText={{ noRowsLabel: t("ledgerReport.noMovements", "لا توجد حركات في الفترة المحددة") }}
          sx={{
            ...reportGridSx,
            "& .row-opening, & .row-opening:hover": { bgcolor: "#f8fafc !important", fontStyle: "normal" },
            "& .row-opening .MuiDataGrid-cell": { borderBottom: "1px dashed #cbd5e1" },
          }}
        />
      </ReportTableCard>
    </ReportPage>
  );
}